import React, { useState } from 'react'
import Button from 'components/shared/Button'
import { Tooltip, Chip, MenuItem, Select } from '@material-ui/core'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import strings from 'strings'

const DEMO_ORG_SUGGESTIONS = ['pharmacy', 'medical', 'health']
const DEMO_INDIVIDUAL_SUGGESTIONS = ['smith', 'jones', 'garcia']

const fieldWrap = { display: 'flex', flexDirection: 'column', flex: '1 1 200px', minWidth: 160 }
const fieldRow = { display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }
const inputStyle = { padding: '6px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14 }
const labelStyle = { fontSize: 12, marginBottom: 4, color: '#555', display: 'flex', alignItems: 'center', gap: 4 }

const TOOLTIPS = {
    npi: 'National Provider Identifier — 10-digit unique ID. If provided, an exact NPI match short-circuits the fuzzy search.',
    dob: 'Date of birth (YYYY-MM-DD). Sent to the backend as YYYYMMDD to match the source\'s native date format.',
    state: 'Restrict to a single state\'s exclusion list. Leave as "All states" to search every state Rox has ingested.',
    providerType: 'Prefix match against the provider type / occupation on the exclusion record.',
}

// Convert a YYYY-MM-DD user input to the YYYYMMDD form the backend expects.
const normalizeDobForPayload = (dob) => {
    if (!dob) return null
    const digits = dob.replace(/[^0-9]/g, '')
    return digits.length === 8 ? digits : dob
}

const StateMedicaidSearchFields = ({ loading, handleSearch, isQuickSearch, isDemoMode, prefillTargets, coverageStates = [] }) => {
    // In an application tab, prefill busName from the business by default; a chip row lets the
    // user swap to search for an individual owner instead.
    const businessPrefill = prefillTargets?.business || ''
    const [npi, setNpi] = useState('')
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [busName, setBusName] = useState(() => businessPrefill)
    const [dob, setDob] = useState('')
    const [stateCode, setStateCode] = useState('')
    const [providerType, setProviderType] = useState('')
    const [selectedPrefill, setSelectedPrefill] = useState(() => businessPrefill ? 'business' : null)

    const canSubmit = npi || firstName || lastName || busName || dob || providerType

    const onSubmit = () => {
        if (loading) return
        handleSearch({
            npi: npi || null,
            firstName: firstName || null,
            lastName: lastName || null,
            busName: busName || null,
            dob: normalizeDobForPayload(dob),
            stateCode: stateCode || null,
            providerType: providerType || null,
        })
    }

    const labelWithTooltip = (text, tooltipText) => (
        <label style={labelStyle}>
            {text}
            {tooltipText && (
                <Tooltip title={tooltipText} arrow placement='top'>
                    <InfoOutlinedIcon style={{ fontSize: 14, color: '#888', cursor: 'help' }} />
                </Tooltip>
            )}
        </label>
    )

    const field = (labelEl, value, setter, id, placeholder = '', type = 'text') => (
        <div style={fieldWrap}>
            {labelEl}
            <input
                style={inputStyle}
                value={value}
                onChange={(e) => setter(e.target.value)}
                placeholder={placeholder}
                id={id}
                type={type}
            />
        </div>
    )

    const applyOrgSuggestion = (term) => {
        setBusName(term)
        setFirstName('')
        setLastName('')
        setNpi('')
    }
    const applyIndividualSuggestion = (term) => {
        setLastName(term)
        setBusName('')
        setNpi('')
    }

    const selectBusiness = () => {
        setBusName(businessPrefill)
        setFirstName('')
        setLastName('')
        setSelectedPrefill('business')
    }
    const selectOwner = (owner, index) => {
        setBusName('')
        setFirstName(owner.firstName || '')
        setLastName(owner.lastName || '')
        setSelectedPrefill(`owner-${index}`)
    }
    const owners = prefillTargets?.owners || []
    const showPrefillPicker = businessPrefill || owners.length > 0
    const prefillChipStyle = (isSelected) => ({
        background: isSelected ? 'var(--rox-lilac-ink)' : 'var(--badge-review-bg)',
        color: isSelected ? '#fff' : 'var(--rox-lilac-ink)',
        fontWeight: isSelected ? 600 : 400,
    })

    return (
        <div>
            {showPrefillPicker && (
                <div style={{ marginBottom: 12, padding: '8px 12px', background: 'var(--bg-canvas)', borderRadius: 4, fontSize: 12 }}>
                    <div style={{ color: '#555', marginBottom: 6 }}>
                        <strong>Prefill from application</strong> — click to switch search target:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {businessPrefill && (
                            <Chip
                                label={`Business: ${businessPrefill}`}
                                size='small'
                                clickable
                                onClick={selectBusiness}
                                style={prefillChipStyle(selectedPrefill === 'business')}
                            />
                        )}
                        {owners.map((owner, i) => (
                            <Chip
                                key={i}
                                label={`Owner: ${owner.label}`}
                                size='small'
                                clickable
                                onClick={() => selectOwner(owner, i)}
                                style={prefillChipStyle(selectedPrefill === `owner-${i}`)}
                            />
                        ))}
                    </div>
                </div>
            )}
            {isDemoMode && (
                <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f0f4ff', borderRadius: 4, fontSize: 12 }}>
                    <div style={{ color: '#555', marginBottom: 6 }}>
                        <strong>Demo mode suggestions</strong> — click to fill:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span style={{ color: '#666', minWidth: 90 }}>Businesses →</span>
                        {DEMO_ORG_SUGGESTIONS.map(term => (
                            <Chip
                                key={term}
                                label={term}
                                size='small'
                                clickable
                                onClick={() => applyOrgSuggestion(term)}
                                style={{ background: '#fff' }}
                            />
                        ))}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                        <span style={{ color: '#666', minWidth: 90 }}>Individuals →</span>
                        {DEMO_INDIVIDUAL_SUGGESTIONS.map(term => (
                            <Chip
                                key={term}
                                label={term}
                                size='small'
                                clickable
                                onClick={() => applyIndividualSuggestion(term)}
                                style={{ background: '#fff' }}
                            />
                        ))}
                    </div>
                </div>
            )}
            <div style={fieldRow}>
                {field(labelWithTooltip('NPI', TOOLTIPS.npi), npi, setNpi, 'stateMedicaidNpi', '10-digit NPI (exact match)')}
                {field(labelWithTooltip('DOB', TOOLTIPS.dob), dob, setDob, 'stateMedicaidDob', 'YYYY-MM-DD')}
                <div style={fieldWrap}>
                    {labelWithTooltip('State', TOOLTIPS.state)}
                    <Select
                        value={stateCode}
                        onChange={(e) => setStateCode(e.target.value)}
                        displayEmpty
                        id='stateMedicaidState'
                        style={{ fontSize: 14 }}
                    >
                        <MenuItem value=''>All states</MenuItem>
                        {coverageStates.map(sc => (
                            <MenuItem key={sc} value={sc}>{sc}</MenuItem>
                        ))}
                    </Select>
                </div>
            </div>
            <div style={fieldRow}>
                {field(labelWithTooltip('First Name'), firstName, setFirstName, 'stateMedicaidFirstName')}
                {field(labelWithTooltip('Last Name'), lastName, setLastName, 'stateMedicaidLastName')}
                {field(labelWithTooltip('Business Name'), busName, setBusName, 'stateMedicaidBusName', 'e.g. Kaiser')}
            </div>
            <div style={fieldRow}>
                {field(labelWithTooltip('Provider Type', TOOLTIPS.providerType), providerType, setProviderType, 'stateMedicaidProviderType', 'e.g. Physician')}
            </div>
            <div className='d-flex align-items-center justify-content-end mt-2' style={{ gap: '16px' }}>
                <Button
                    onClick={onSubmit}
                    loading={loading}
                    disabled={!canSubmit}
                    id={`perform-state-medicaid-search-${isQuickSearch ? 'qs' : 'app'}`}
                    style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                    {strings.SEARCH}
                </Button>
            </div>
        </div>
    )
}

export default StateMedicaidSearchFields
